import React, { FC, useRef } from 'react';
import { hasExternalForm } from '@/helpers/externalForm';
import { ModalName } from '@/common/contexts/globalModalContext';
import ListingLayout, { ListingLayoutProps } from '@/common/layouts/ListingLayout/ListingLayout';
import VPSection from '@/pages/MarketReportsListingPage/components/VPSection/VPSection';
import { BannerProps } from '@/common/components/Banner/Banner';
import ListingGallery from '@/common/components/ListingGallery/ListingGallery';
import { ListCardProps } from '@/common/components/ListCard/ListCard';
import { VPOptionProps } from '@/pages/MarketReportsListingPage/components/VPOption/VPOption';
import SearchBar from '@/pages/MarketReportsListingPage/components/SearchBar/SearchBar';
import ExternalModalForms from '@/common/components/Modal/ExternalModalForms/ExternalModalForms';
import CustomReportModal, { CustomReportModalProps } from '@/common/components/CustomReportModal/CustomReportModal';
import styles from './MarketReportsListingPage.module.scss';

interface MarketReportsListingPageProps extends ListingLayoutProps {
  vpSection?: VPOptionProps[];
  featuredReportsBlock?: ReportsBlock;
  locationReportsBlock?: ReportsBlock;
  banner?: BannerProps;
  customInsightPopup?: CustomReportModalProps;
  addressForSearch?: string;
}

interface ReportsBlock {
  title: string;
  mark: string;
  invalidLocation: string | null;
  reports: ListCardProps[];
}

const MarketReportsListingPage: FC<MarketReportsListingPageProps> = ({
  featuredReportsBlock,
  locationReportsBlock,
  vpSection,
  banner,
  customInsightPopup,
  addressForSearch,
  pardot,
  signUpSection,
  ...listingLayoutProps
}) => {
  const {
    title: featuredTitle,
    reports: featuredReports,
    mark: featuredMark,
  } = featuredReportsBlock || {};
  const {
    title: locationsTitle,
    reports: locationReports,
    mark: searchBarPlaceholder,
    invalidLocation,
  } = locationReportsBlock || {};

  const itemRefToScroll = useRef<HTMLDivElement | null>(null);

  const shouldRenderNewsletterModal = !hasExternalForm({ pardot, modalName: ModalName.newsletter });

  const featuredReportCards = featuredReports?.map((featuredReport): ListCardProps => ({
    mark: featuredMark, ...featuredReport,
  }));

  const renderModal = () => {
    if (!customInsightPopup || hasExternalForm({ pardot, modalName: ModalName.customReport })) {
      return null;
    }

    return (
      <CustomReportModal {...customInsightPopup} />
    );
  };

  return (
    <ListingLayout
      itemRefToScroll={itemRefToScroll}
      shouldRenderNewsletterModal={shouldRenderNewsletterModal}
      signUpSection={signUpSection}
      {...listingLayoutProps}
    >
      {!!vpSection && (
        <VPSection options={vpSection} />
      )}
      <div className={styles.featuredTitle}>
        {featuredTitle}
      </div>
      <ListingGallery cards={featuredReportCards} />

      <div ref={itemRefToScroll} className={styles.searchBarContainer}>
        <div className={styles.locationsTitle}>
          {locationsTitle}
        </div>
        <SearchBar
          currentSearch={addressForSearch}
          invalidMessage={invalidLocation || ''}
          placeholder={searchBarPlaceholder}
        />
      </div>

      <ListingGallery
        banner={banner}
        cards={locationReports}
      />
      {!!pardot && (
        <ExternalModalForms
          pardot={pardot}
          titles={{
            [ModalName.customReport]: customInsightPopup?.modalWindowTitle,
            [ModalName.newsletter]: signUpSection?.modalWindowTitle,
          }}
        />
      )}
      {renderModal()}
    </ListingLayout>
  );
};

export default MarketReportsListingPage;
